import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';
import { type FileWithPath } from 'react-dropzone';

import { type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { toAnimatedOnboardingUploadFiles } from '~/components/WorkspaceOnboardingForm/toAnimatedOnboardingUploadFiles';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PendingOnboardingUploadHandoff = {
  board: Board | undefined;
  files: readonly FileWithPath[];
  skipBoardCreation: boolean;
  workspaceId: string;
};

export type PendingOnboardingUploadFilesContextValue = {
  clearPendingOnboardingUploadFiles: () => void;
  setPendingOnboardingUploadBoard: (board: Board) => void;
  setPendingOnboardingUploadFiles: (files: readonly File[], workspaceId: string) => void;
  setPendingOnboardingUploadSkipBoardCreation: () => void;
  takePendingOnboardingUploadFiles: () => PendingOnboardingUploadHandoff | undefined;
};

const defaultContextValue: PendingOnboardingUploadFilesContextValue = {
  clearPendingOnboardingUploadFiles: () => undefined,
  setPendingOnboardingUploadBoard: () => undefined,
  setPendingOnboardingUploadFiles: () => undefined,
  setPendingOnboardingUploadSkipBoardCreation: () => undefined,
  takePendingOnboardingUploadFiles: () => undefined,
};

const PendingOnboardingUploadFilesContext =
  createContext<PendingOnboardingUploadFilesContextValue>(defaultContextValue);

export const usePendingOnboardingUploadFiles = () => {
  const context = useContext(PendingOnboardingUploadFilesContext);

  if (context === defaultContextValue) {
    const error = new Error('usePendingOnboardingUploadFiles must be used within PendingOnboardingUploadFilesProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Pending onboarding upload files context not found',
    });
  }

  return context;
};

export type PendingOnboardingUploadFilesProviderProps = {
  children: ReactNode;
};

export const PendingOnboardingUploadFilesProvider = memo(({ children }: PendingOnboardingUploadFilesProviderProps) => {
  const filesRef = useRef<readonly FileWithPath[]>([]);
  const boardRef = useRef<Board | undefined>(undefined);
  const skipBoardCreationRef = useRef<boolean>(false);
  const workspaceIdRef = useRef<string | undefined>(undefined);

  const setPendingOnboardingUploadFiles = useCallback((files: readonly File[], workspaceId: string) => {
    filesRef.current = toAnimatedOnboardingUploadFiles([...files]);
    boardRef.current = undefined;
    skipBoardCreationRef.current = false;
    workspaceIdRef.current = workspaceId;
  }, []);

  const setPendingOnboardingUploadBoard = useCallback((board: Board) => {
    boardRef.current = board;
  }, []);

  const setPendingOnboardingUploadSkipBoardCreation = useCallback(() => {
    skipBoardCreationRef.current = true;
  }, []);

  const takePendingOnboardingUploadFiles = useCallback(() => {
    const files = filesRef.current;
    const board = boardRef.current;
    const skipBoardCreation = skipBoardCreationRef.current;
    const workspaceId = workspaceIdRef.current;
    filesRef.current = [];
    boardRef.current = undefined;
    skipBoardCreationRef.current = false;
    workspaceIdRef.current = undefined;

    if (files.length === 0 || !workspaceId) {
      return undefined;
    }

    const handoff: PendingOnboardingUploadHandoff = {
      board,
      files,
      skipBoardCreation,
      workspaceId,
    };

    return handoff;
  }, []);

  const clearPendingOnboardingUploadFiles = useCallback(() => {
    filesRef.current = [];
    boardRef.current = undefined;
    skipBoardCreationRef.current = false;
    workspaceIdRef.current = undefined;
  }, []);

  const contextValue = useMemo(
    () => ({
      clearPendingOnboardingUploadFiles,
      setPendingOnboardingUploadBoard,
      setPendingOnboardingUploadFiles,
      setPendingOnboardingUploadSkipBoardCreation,
      takePendingOnboardingUploadFiles,
    }),
    [
      clearPendingOnboardingUploadFiles,
      setPendingOnboardingUploadBoard,
      setPendingOnboardingUploadFiles,
      setPendingOnboardingUploadSkipBoardCreation,
      takePendingOnboardingUploadFiles,
    ],
  );

  return (
    <PendingOnboardingUploadFilesContext.Provider value={contextValue}>
      {children}
    </PendingOnboardingUploadFilesContext.Provider>
  );
});

PendingOnboardingUploadFilesProvider.displayName = 'PendingOnboardingUploadFilesProvider';
